import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import CheckCircle from "@mui/icons-material/CheckCircle";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useForm, Controller } from "react-hook-form";
import {
  Button as UIButton,
  FormControlLabel,
  Switch,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import dateformat from "dateformat";
import { createUseStyles } from "$utils/createUseStyles";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import CalendarCheckFillIcon from "remixicon-react/CalendarEventFillIcon";
import UserLineIcon from "remixicon-react/UserLineIcon";
import StickyNoteLineIcon from "remixicon-react/StickyNoteLineIcon";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "$utils/useSnackbar";
import { theme } from "$utils/theme/bataillog";
import { Text, Layout, Popin, Button, InfoContainer, Modal } from "$components";
import { writetoLS, DEFAULT_DATE_FORMAT } from "$utils/utils";
import { DockyardServices, EDBService } from "$services";
import Notes from "$components/Notes";
import useJStyles from "$hooks/useJStyles";
import useDockyardForm from "$hooks/useDockyardForm";
import DockyardForm from "../DockyardForm";
import FileUploader from "$components/FileUploader/FileUploader";
import useAuth from "$hooks/useAuth";
import { snackbarErrorOptions } from "$utils/SnackbarOptions";
import { IDockyard, deleteDockyard } from "$services/Dockyard.service";
import DockyardIcon from "../DockyardIcon";
import Orders from "./Orders";
import DockyardStatusBadge from "./DockyardStatusBadge";
import EDBs from "./EDBs";
import { Loader } from "$components/Layout/Loader";
import { CacheInvalidationAction } from "$utils/cache";
import JTooltip from "$components/JTooltip/JTooltip";

const DOCKYARD_DEACTIVATION_BLOCKED_MESSAGE =
  "Ce chantier ne peut pas être désactivé car des EDB ou préparations sont en cours.";

const isDockyardDeactivationBlockedError = (message?: string | null) =>
  !!message?.includes("Impossible de désactiver ce chantier");

export interface IDockyardProps extends IDockyard {
  className?: string;
}

function DockyardDetail() {
  const param = useParams<"idChantier">();
  const { idChantier } = param;
  const { isAdmin, isClient } = useAuth();
  const { token, isContributor } = useAuth();
  const navigate = useNavigate();
  const classes = useStyles();
  const { jclasses } = useJStyles();
  const [modal, setModal] = useState<boolean>(false);
  const [modalActive, setModalActive] = useState<boolean>(false);
  const [modalDeleteOpen, setModalDeleteOpen] = useState<boolean>(false);
  const [hasOrders, setHasOrders] = useState<boolean>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [openSnackbarError] = useSnackbar(snackbarErrorOptions);

  const { data: dockyard, refetch: refetchDockyard } = useQuery({
    queryKey: [CacheInvalidationAction.GET_DOCKYARD_BY_ID, idChantier],
    queryFn: () => DockyardServices.getDockyardById(idChantier),
    enabled: !!idChantier,
    staleTime: 1000 * 60,
  });
  const { data: edbList, isLoading } = useQuery({
    queryKey: [CacheInvalidationAction.GET_EDBS, { dockyard: idChantier }],
    queryFn: () => EDBService.getEDBs({ dockyard: idChantier }),
    enabled: !!idChantier,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const isDone = dockyard?.status === "done";
  const canEdit = isAdmin();
  const isDockyardDisabled = dockyard?.isDisable || false;
  const canToggleDockyard =
    canEdit && !dockyard?.projectDisable && !dockyard?.companyDisable;
  const isFullyDisabled =
    isDone ||
    isDockyardDisabled ||
    dockyard?.projectDisable ||
    dockyard?.companyDisable ||
    !canEdit;

  const hasEdbLeft = !(edbList?.data || []).every(
    (edb) =>
      edb.status?.includes("collected") || edb.status?.includes("delivered")
  );

  const [formValues, setFormValues] = useState<any>({
    medias: (dockyard?.medias || []).map((media: any) => media || {}),
  });
  const { control, setValue } = useForm({
    mode: "onChange",
  });

  useEffect(() => {
    setFormValues({
      medias: (dockyard?.medias || []).map((media: any) => media || {}),
    });
  }, [dockyard]);

  useEffect(() => {
    setValue("medias", formValues.medias, { shouldValidate: false });
  }, [formValues.medias, setValue, formValues]);

  const disableDockyardMutation = useMutation({
    mutationFn: () =>
      DockyardServices.DisabledDockyard(token, idChantier || ""),
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Une erreur est survenue lors de la désactivation du chantier";
      openSnackbarError(
        isDockyardDeactivationBlockedError(message)
          ? DOCKYARD_DEACTIVATION_BLOCKED_MESSAGE
          : message
      );
      setModalActive(false);
    },
    onSuccess: (result: any) => {
      if (result?.error) {
        openSnackbarError(
          isDockyardDeactivationBlockedError(result.error)
            ? DOCKYARD_DEACTIVATION_BLOCKED_MESSAGE
            : result.error
        );
        setModalActive(false);
        return;
      }
      setModalActive(false);
      refetchDockyard();
      queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_EDBS],
      });
      setErrorMessage(null);
    },
  });

  const enableDockyardMutation = useMutation({
    mutationFn: () => DockyardServices.EnableDockyard(token, idChantier || ""),
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Une erreur est survenue lors de l'activation du chantier";
      setErrorMessage(message);
      setModalActive(false);
    },
    onSuccess: () => {
      setModalActive(false);
      refetchDockyard();
      queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_EDBS],
      });
      setErrorMessage(null);
    },
  });

  const {
    classes: dockyardFormClasses,
    togglePopin,
    openPopin: openPopinDockyard,
    handleSubmit,
    isOpened,
    submitting,
    error,
    dockyard: formDockyard,
  } = useDockyardForm();

  if (!idChantier) {
    return null;
  }

  const handleSwitchToggle = async () => {
    setModalActive(true);
  };

  const DisableDockyard = async () => {
    await disableDockyardMutation.mutateAsync();
  };

  const EnableDockyard = async () => {
    const dkProj: any = dockyard?.project;
    if (
      !dkProj?.isDisable &&
      !dkProj?.companyDisable &&
      dkProj?.status !== "done"
    ) {
      await enableDockyardMutation.mutateAsync();
    }
  };

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  const handleClickChantier = async () => {
    if (dockyard) {
      await openPopinDockyard(dockyard._id || (dockyard as any).id);
    }
  };

  // render Header Layout
  const renderHead = () => (
    <div
      style={{
        display: "flex",
        marginLeft: 18,
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <div className={classes.customHeader}>
        <div className={classes.calendar}>
          <CalendarCheckFillIcon className={classes.calendarIcon} />
          Créé le{" "}
          {(dockyard as any)?.createdAt &&
            dateformat(
              new Date((dockyard as any)?.createdAt),
              DEFAULT_DATE_FORMAT
            )}
        </div>
        <div className={classes.statusBadge}>
          <DockyardStatusBadge
            status={dockyard?.status}
            disabled={isFullyDisabled}
          />
        </div>
        <JTooltip
          title={
            dockyard?.projectDisable
              ? "Le chantier est inactif car le projet associé est inactif"
              : dockyard?.companyDisable
                ? "Le chantier est inactif car l'entreprise associée est inactive"
                : ""
          }
        >
          <FormControlLabel
            className={classes.switchButton}
            control={
              <Switch
                checked={!isDockyardDisabled}
                onChange={handleSwitchToggle}
                name="activeCompany"
                color="primary"
                disabled={
                  isContributor() ||
                  !canToggleDockyard ||
                  disableDockyardMutation.isPending ||
                  enableDockyardMutation.isPending
                }
              />
            }
            label="Chantier actif"
          />
        </JTooltip>
        <div />
      </div>
      <Tooltip
        title={
          isDone
            ? "Vous ne pouvez pas supprimer un chantier terminé"
            : edbList?.data?.length > 0 || hasOrders === true
              ? "Vous ne pouvez pas supprimer ce chantier car il a des expressions de besoin existantes"
              : isFullyDisabled
                ? "Vous ne pouvez pas supprimer un chantier inactif"
                : ""
        }
      >
        <span>
          <Button
            style={{ marginRight: 40, marginTop: 20 }}
            onClick={() => setModalDeleteOpen(true)}
            className={[
              classes.buttonDelete,
              edbList?.data?.length > 0 || hasOrders === true || isFullyDisabled
                ? "disabled"
                : "",
            ].join(" ")}
            disabled={
              (isContributor() && !isClient()) || !canEdit || isFullyDisabled
            }
          >
            Supprimer le chantier
          </Button>
        </span>
      </Tooltip>
      <JTooltip
        title={
          isFullyDisabled
            ? "Vous ne pouvez pas terminer un chantier inactif"
            : hasEdbLeft
              ? "Vous ne pouvez pas terminer ce chantier car il reste des expressions de besoin non collectées ou non livrées"
              : ""
        }
      >
        <span>
          <Button
            onClick={() => setModal(true)}
            className={[
              classes.BtnDone,
              isDone
                ? "disabled"
                : hasEdbLeft || isFullyDisabled
                  ? "disabled"
                  : "",
            ].join(" ")}
            disabled={
              (isContributor() && !isClient()) || !canEdit || isFullyDisabled
            }
          >
            <CheckCircle style={{ marginRight: "5px" }} />
            {!isDone
              ? "Terminer mon chantier"
              : `Terminé le ${dateformat(
                  new Date(dockyard?.updateAt || "").toDateString(),
                  DEFAULT_DATE_FORMAT
                )}`}
          </Button>
        </span>
      </JTooltip>
    </div>
  );

  const handleClickTerminate = async () => {
    await DockyardServices.UpdateDockyardStatus(
      token,
      { status: "done" },
      idChantier || ""
    );
    setModal(false);
  };

  const handleDeleteDockyard = async () => {
    await deleteDockyard(token, dockyard._id);
    navigate("/chantiers");
  };

  const handleEdbRedirect = async () => {
    await writetoLS("dockyard", JSON.stringify(dockyard));
    navigate("/create-edb");
  };

  const EdditButton = () => (
    <JTooltip
      title={
        isFullyDisabled
          ? canEdit
            ? "Vous ne pouvez pas éditer un chantier inactif."
            : "Vous n'avez pas les droits pour éditer ce chantier."
          : ""
      }
    >
      <UIButton
        className={classes.editButton}
        disabled={isFullyDisabled}
        onClick={handleClickChantier}
      >
        <Edit2LineIcon />
      </UIButton>
    </JTooltip>
  );

  const filteredInterveningCompanies = dockyard?.interveningCompany?.filter(
    (c) => c.isActive
  );

  return (
    <Layout
      renderHead={renderHead}
      title={dockyard?.name || ""}
      contentClassName={dockyardFormClasses.LayoutCustom}
    >
      <div className={classes.ContainerInfo} style={{ display: "flex" }}>
        <div
          style={{
            marginRight: 18,
            marginBottom: 18,
            minWidth: "58%",
            maxWidth: "58%",
            display: "flex",
          }}
        >
          <InfoContainer
            className={[classes.informationHeader, classes.InfoHeight].join(
              " "
            )}
            leftHeader={
              <div className={classes.infoHeader}>
                <DockyardIcon />
                <Text type="h3">Informations chantier</Text>
              </div>
            }
            rightHeader={<EdditButton />}
          >
            <div className="about">
              <div className="left">
                <table>
                  <tr>
                    <td className="label">Code projet</td>
                    <td className="data">
                      {" "}
                      {(dockyard?.project as any)?.code || ""}
                    </td>
                  </tr>
                  <tr>
                    <td className="label">Nom projet</td>
                    <td className="data">
                      <Link
                        to={`/chantiers/${idChantier}/projets/${
                          (dockyard?.project as any)?._id
                        }`}
                        className={jclasses.ghostLink}
                      >
                        {(dockyard?.project as any)?.name || ""}
                      </Link>
                    </td>
                  </tr>
                  <tr>
                    <td className="label">Code chantier</td>
                    <td className="data">{dockyard?.code || ""}</td>
                  </tr>
                  <tr>
                    <td className="label">Nom chantier</td>
                    <td className="data">{dockyard?.name || ""}</td>
                  </tr>
                  {dockyard?.codeAnalytics && (
                    <tr>
                      <td className="label">Code analytique</td>
                      <td className="data">
                        {dockyard?.codeAnalytics || "N/A"}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="label">Adresse</td>
                    <td className="data">
                      {dockyard &&
                        (!dockyard.address &&
                        !dockyard.address2 &&
                        !dockyard.postalCode &&
                        !dockyard.country ? (
                          <div className="div">Non renseignée</div>
                        ) : (
                          <>
                            <div className="div">{dockyard?.address || ""}</div>
                            {dockyard?.address2 && (
                              <div className="div">
                                {dockyard?.address2 || ""}
                              </div>
                            )}
                            <div className="div">{`${
                              dockyard?.postalCode || ""
                            } ${(dockyard?.city || "").toUpperCase()}`}</div>
                            <div className="div">
                              {(dockyard?.country || "").toUpperCase()}
                            </div>
                          </>
                        ))}
                    </td>
                  </tr>
                  <tr>
                    <td className="label">Entreprise(s) intervenante(s)</td>
                    {dockyard && (
                      <td className="data">
                        {filteredInterveningCompanies?.map((c, idx) => (
                          <Link
                            to={`/entreprise/${c?._id}`}
                            className={jclasses.ghostLink}
                            key={c._id}
                          >
                            {c.name}
                            {idx !== filteredInterveningCompanies.length - 1 &&
                              " | "}
                          </Link>
                        ))}
                      </td>
                    )}
                  </tr>
                </table>
              </div>
            </div>
          </InfoContainer>
        </div>

        <div style={{ flex: 1 }}>
          <InfoContainer
            className={[classes.informationHeader].join(" ")}
            leftHeader={
              <div className={classes.infoHeader}>
                <UserLineIcon />
                <Text type="h3">Informations contact</Text>
              </div>
            }
            rightHeader={<EdditButton />}
          >
            <div className="about">
              <div className="left">
                <table>
                  <tr>
                    <td className="label">Entreprise</td>
                    <td className="data">
                      {(dockyard?.company as any)?.name || ""}
                    </td>
                  </tr>
                  <tr>
                    <td className="label">Approbateur</td>
                    <td className="data">
                      {(dockyard as any)?.approver
                        ? `${(dockyard as any).approver.firstname || ""} ${
                            (
                              dockyard as any
                            ).approver.lastname?.toUpperCase() || ""
                          }`.trim()
                        : "Non renseigné"}
                    </td>
                  </tr>
                  <tr>
                    <td className="label">Email approbateur</td>
                    <td className="data">
                      {(dockyard as any)?.approver?.email || ""}
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </InfoContainer>

          <div style={{ marginTop: 18 }} />

          <InfoContainer
            className={[
              classes.informationHeader,
              // clasx.InfoHeight
            ].join(" ")}
            leftHeader={
              <div className={classes.infoHeader}>
                <StickyNoteLineIcon />
                <Text type="h3">Commentaires / notes</Text>
              </div>
            }
            rightHeader={<EdditButton />}
          >
            <Notes text={dockyard?.notes} />
          </InfoContainer>

          <div style={{ marginTop: 18 }} />
        </div>
      </div>
      {isLoading && <Loader />}
      {!isLoading && (
        <EDBs
          dockyard={dockyard}
          edbs={edbList?.data || []}
          handleEdbRedirect={handleEdbRedirect}
          dockyardId={idChantier}
        />
      )}
      <InfoContainer
        leftHeader={
          <div className={classes.infoHeader}>
            <AttachFileIcon />
            <Text type="h3">Pièces jointes</Text>
          </div>
        }
        className={classes.infoContainer}
      >
        {dockyard?.medias?.length > 0 && (
          <div>
            <Text type="h3">Liste des médias</Text>
          </div>
        )}
        <form>
          <Controller
            control={control}
            name="medias"
            render={({ field: { onChange, value } }) => (
              <FileUploader
                value={value}
                onChange={(values) => {
                  onChange(values);
                }}
                isAdmin={isAdmin()}
                type="dockyard"
                company={(dockyard?.company as any)?._id}
                id={dockyard?._id ?? undefined}
                isEdit={false}
                isLargeVersion
                isReadOnly={isFullyDisabled || isContributor()}
              />
            )}
          />
        </form>
      </InfoContainer>

      <Orders dockyardId={idChantier} setHasOrders={setHasOrders} />

      <Modal
        open={modalActive}
        onCancel={() => setModalActive(false)}
        className={classes.modal}
        title="Attention"
        textValidate={isDockyardDisabled ? "Activer" : "Désactiver"}
        onConfirm={isDockyardDisabled ? EnableDockyard : DisableDockyard}
      >
        <div className={classes.ModalContainer}>
          {`Vous êtes sur le point de ${
            isDockyardDisabled ? "réactiver" : "désactiver"
          } ce chantier, êtes-vous sûr de vouloir continuer ?`}
        </div>
      </Modal>
      <Modal
        open={modal}
        onCancel={() => setModal(false)}
        className={classes.modal}
        title="Attention"
        textValidate="Chantier terminé"
        onConfirm={handleClickTerminate}
      >
        <div className={classes.ModalContainer}>
          Vous êtes sur le point de marquer votre chantier comme
          &quot;Terminé&quot;. Êtes-vous sûr de vouloir confirmer cette action ?
        </div>
      </Modal>
      <Popin
        className={dockyardFormClasses.ProjectsPage_Popin}
        openPopin={isOpened}
        toggleDrawer={togglePopin}
      >
        <DockyardForm
          dockyard={formDockyard}
          defaultValuesForm={null}
          onSubmit={handleSubmit}
          onCancel={() => togglePopin(false)}
          error={error}
          isSubmitting={submitting}
        />
      </Popin>
      <Modal
        open={modalDeleteOpen}
        onCancel={() => setModalDeleteOpen(false)}
        onValidate={async () => {
          setModalDeleteOpen(false);
          await handleDeleteDockyard();
        }}
        textCancel="Annuler"
        textValidate="Supprimer"
      >
        <div style={{ textAlign: "center" }}>
          <Text>
            Vous êtes sur le point de supprimer définitivement ce chantier.
            <br />
            Êtes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%", fontSize: "14px" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default DockyardDetail;

const useStyles = createUseStyles(() => ({
  editButton: {
    color: "rgba(0, 0, 0) !important",
    "&:hover": {
      color: "rgba(0, 0, 0) !important",
      backgroundColor: "rgba(0, 0, 0, 0.04) !important",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.26) !important",
    },
    "& svg": {
      color: "inherit !important",
    },
  },
  BtnDone: {
    position: "absolute !important",
    top: "80px",
    right: "40px",
    "&.disabled": {
      pointerEvents: "none !important",
      background: "#ccc",
      cursor: "unset",
      "&:hover, &:focus, &:active": {
        background: "#ccc",
      },
    },
  },
  ContainerInfo: {
    position: "relative",
  },
  text: {
    marginLeft: 5,
  },
  infoHeader: {
    display: "flex",
    alignItems: "center",
    "& h3": {
      margin: "0 8px",
    },
  },
  InfoHeight: {
    minHeight: "395px",
    '& div[class="bodyContainer*"]': {
      minHeight: "335px",
    },
  },
  customHeader: {
    display: "flex",
    marginLeft: 18,
    alignItems: "center",
  },
  calendar: {
    display: "flex",
    padding: "0 18px",
    alignItems: "center",
    borderLeft: `1px solid ${theme.color.gray2}`,
    borderRight: `1px solid ${theme.color.gray2}`,
    color: theme.color.gray5,
    fontSize: 14,
    marginRight: 18,
  },
  calendarIcon: {
    width: 18,
    marginRight: 8,
  },
  statusBadge: {
    borderRight: `1px solid ${theme.color.gray2}`,
    paddingRight: "18px",
    marginRight: "18px",
  },
  switchButton: {
    "& span": {
      fontSize: 14,
    },
  },
  infoContainer: {
    marginTop: 10,
    marginBottom: 20,
  },
  informationHeader: {
    marginRight: 18,
    width: "100%",
    "& .about": {
      display: "flex",
      "& .left, & .right": {
        flex: 1,
      },
      "& .left": {
        paddingRight: "10px",
      },
      "& .right": {
        marginLeft: "10px",
      },
      "& table": {
        width: "100%",
        borderSpacing: "0 20px",
      },
      "& .label": {
        color: theme.color.black,
        width: "50%",
        verticalAlign: "top",
      },
      "& .data": {
        color: theme.color.blue,
        fontWeight: "bold",
        width: "50%",
        verticalAlign: "top",
      },
    },
  },
  Low: {
    color: theme.color.red,
  },
  Medium: {
    color: theme.color.orange,
  },
  High: {
    color: theme.color.green,
  },
  modal: {
    display: "flex",
    padding: theme.spacing(1),
    alignItems: "center",
    justifyContent: "center",
    "& .MuiDialog-paperWidthSm": {
      maxWidth: "508px !important",
    },
    "& .MuiDialogActions-spacing": {
      justifyContent: "space-between !important",
    },
  },
  ModalContainer: {
    padding: "0 50px",
    textAlign: "center",
  },
  buttonDelete: {
    "&.disabled": {
      pointerEvents: "none !important",
      background: "#ccc",
      cursor: "unset",
      "&:hover, &:focus, &:active": {
        background: "#ccc",
      },
    },
    marginRight: 40,
  },
}));
